import { MakeLogicType, actions, kea, listeners, path, selectors } from 'kea'
import { loaders } from 'kea-loaders'
import posthog from 'posthog-js'

import { lemonToast } from '@posthog/lemon-ui'

import api from 'lib/api'
import { getAppContext } from 'lib/utils/getAppContext'

import { UserProductListItem } from '~/queries/schema/schema-general'

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface customProductsLogicValues {
    customProducts: UserProductListItem[]
    customProductsLoading: boolean
    enabledToolPaths: Set<string>
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface customProductsLogicActions {
    loadCustomProducts: () => any
    loadCustomProductsFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadCustomProductsSuccess: (
        customProducts: UserProductListItem[],
        payload?: any
    ) => {
        customProducts: UserProductListItem[]
        payload?: any
    }
    setToolEnabled: (
        productPath: string,
        enabled: boolean
    ) => {
        enabled: boolean
        productPath: string
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface customProductsLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        enabledToolPaths: (customProducts: UserProductListItem[]) => Set<string>
    }
}

export type customProductsLogicType = MakeLogicType<
    customProductsLogicValues,
    customProductsLogicActions,
    Record<string, any>,
    customProductsLogicMeta
>

export const customProductsLogic = kea<customProductsLogicType>([
    path(['layout', 'panel-layout', 'ProjectTree', 'customProductsLogic']),
    actions({
        setToolEnabled: (productPath: string, enabled: boolean) => ({ productPath, enabled }),
    }),
    loaders(() => ({
        customProducts: [
            getAppContext()?.custom_products ?? [],
            {
                loadCustomProducts: async (): Promise<UserProductListItem[]> => {
                    const response = await api.userProductList.list()

                    return response.results ?? []
                },
            },
        ],
    })),
    selectors({
        enabledToolPaths: [
            (s) => [s.customProducts],
            (customProducts: UserProductListItem[]): Set<string> =>
                new Set(customProducts.map((item) => item.product_path)),
        ],
    }),
    listeners(({ actions, values }) => ({
        setToolEnabled: async ({ productPath, enabled }) => {
            posthog.capture('sidebar my tools changed', { product_path: productPath, enabled })
            // Placeholder row: every consumer reads `product_path` only, so the fields the server
            // would fill in are never looked at and there is nothing to reconcile on success.
            const now = new Date().toISOString()
            const withoutTool = values.customProducts.filter((item) => item.product_path !== productPath)
            actions.loadCustomProductsSuccess(
                enabled
                    ? [
                          ...withoutTool,
                          {
                              id: '',
                              product_path: productPath,
                              enabled: true,
                              created_at: now,
                              updated_at: now,
                          },
                      ]
                    : withoutTool
            )
            try {
                await api.userProductList.bulkUpdate([{ product_path: productPath, enabled }])
            } catch (error) {
                console.error('Failed to save tool changes:', error)
                lemonToast.error('Failed to save some changes. Try again?')
                // Only refetch to undo the failed toggle. Refetching on success would race a
                // toggle the user made while this call was in flight, reverting it on screen.
                actions.loadCustomProducts()
            }
        },
    })),
])
